import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api/Middleware';
import { TURNSTILE_CONFIG_URL } from '../api/Urls';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export const useTurnstile = () => {
  const [config, setConfig] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const configRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  // Load Turnstile configuration from API
  const loadConfig = useCallback(async () => {
    if (configRef.current) {
      setConfig(configRef.current);
      setIsConfigLoaded(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get(TURNSTILE_CONFIG_URL);
      const configData = response.data;
      configRef.current = configData;
      setConfig(configData);
      setIsConfigLoaded(true);
      setError(null);
    } catch (err) {
      console.warn('Failed to load Turnstile config:', err);
      
      let errorMessage = 'Failed to load security configuration';
      const fallbackConfig = {
        enabled: false,
        site_key: null
      };
      
      // Tratamento específico por tipo de erro
      if (err.response) {
        const status = err.response.status;
        if (status === 404) {
          console.info('Turnstile endpoint not found - feature not implemented on server');
          errorMessage = null; // Não é um erro real, apenas não implementado
        } else if (status >= 500) {
          console.error('Server error loading Turnstile config');
          errorMessage = 'Server error loading security configuration';
        } else if (status === 403) {
          console.warn('Access denied to Turnstile config');
          errorMessage = 'Access denied to security configuration';
        }
      } else if (err.code === 'NETWORK_ERROR') {
        console.error('Network error loading Turnstile config');
        errorMessage = 'Network error loading security configuration';
      }
      
      configRef.current = fallbackConfig;
      setConfig(fallbackConfig);
      setIsConfigLoaded(true);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load Turnstile script
  const loadScript = useCallback(() => {
    if (scriptLoadedRef.current) {
      setIsScriptLoaded(true);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById('turnstile-script');
      if (existingScript) {
        scriptLoadedRef.current = true;
        setIsScriptLoaded(true);
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;

      script.onload = () => {
        scriptLoadedRef.current = true;
        setIsScriptLoaded(true);
        resolve();
      };

      script.onerror = () => {
        console.error('Failed to load Turnstile script');
        setError('Failed to load security verification script');
        reject(new Error('Script load failed'));
      };

      document.head.appendChild(script);
    });
  }, []);

  // Initialize hook
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Load script when config is loaded and Turnstile is enabled
  useEffect(() => {
    if (isConfigLoaded && config?.enabled && !isScriptLoaded) {
      loadScript().catch(err => {
        console.error('Script loading failed:', err);
      });
    }
  }, [isConfigLoaded, config, isScriptLoaded, loadScript]);

  // Reset token
  const resetToken = useCallback(() => {
    setToken(null);
    setError(null);
  }, []);

  // Clear all state
  const clearState = useCallback(() => {
    setToken(null);
    setError(null);
  }, []);

  // Check if Turnstile is ready to be used
  const isReady = isConfigLoaded && 
                  config?.enabled && 
                  isScriptLoaded && 
                  typeof window !== 'undefined' &&
                  window.turnstile && 
                  typeof window.turnstile.render === 'function';
  
  // Check if Turnstile should be shown (enabled and ready or loading)
  const shouldShow = config?.enabled === true;

  return {
    config,
    isScriptLoaded,
    isConfigLoaded,
    isLoading,
    isReady,
    shouldShow,
    token,
    error,
    resetToken,
    clearState,
    loadScript
  };
};